import React from 'react'
import DocumentTitle from 'react-document-title'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import ym, { YMInitializer } from 'react-yandex-metrika'
import parse from 'url-parse'
import { AmplitudeUserProp } from "~/amplitude-counter"
import CookieModal from "~/components/cookie-modal"
import FooterUZ from "~/components/footer-uz"
import { WhatsappWidget } from "~/components/whatsapp-widget"
import { useAppSelector } from "~/hooks/redux"
import { getTitle } from '~helpers/get-title'
import Footer from './footer'
import Menu from './menu'
import About from './pages/about'
import Contacts from './pages/contacts'
import Documents from './pages/documents'
import ExpiredLink from './pages/expired-link'
import Features from './pages/features'
import Detail from './pages/features/components/detail'
import Help from './pages/help'
import { IndexPage } from './pages/index'
import NewYear from './pages/new-year'
import PageNotFound from './pages/page-not-found'
import PartnersRouter from './pages/partners'
import Phoner404 from './pages/phoner-404'
import Price from './pages/price'
import Stories from './pages/stories'
import StoriesCompanies from './pages/stories/companyes'
import ChatsPage from './pages/chats'
import HomeRedirect from "~/components/pages/home-redirect/home-redirect";
import PriceRedirect from "~/components/pages/price-redirect/price-redirect";

export const routes = [
  { path: '/', component: HomeRedirect },
  { path: '/help', component: Help },
  { path: '/price', component: PriceRedirect },
  { path: '/about', component: About },
  { path: '/docs', component: Documents },
  { path: '/404', component: PageNotFound },
  { path: '/404/phoner', component: Phoner404 },
  { path: '/expired-link', component: ExpiredLink },
  { path: '/partners', component: PartnersRouter },
  { path: '/contacts', component: Contacts },
  { path: '/:view(stories)', component: Stories },
  { path: '/:view(stories)/:subview(max_dance|barbershop|energy_fit|igrolandia|online_school)', component: StoriesCompanies },
  { path: '/:view(features)', component: Features },
  { path: '/:view(features)/:subview(calls|admin|reports|autoinformator|auto_call|quality)', component: Detail },
  { path: '/:view(features)/:subview(chats)', component: ChatsPage },
  // { path: '/new-year', component: NewYear },
]

export const MainRouter: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { country } = useAppSelector(state => state.countryReducer);

  const [cookiesAgreed, setCookiesAgreed] = React.useState(true)

  const hasCookiesAgreed = (): boolean => {
    const cookiesArray = document.cookie.split(';')
    const cookieAgreement = cookiesArray.find(item => item.includes('accepted_cookie_agreement='))
    if (cookieAgreement) return cookieAgreement.trim().split('=')[1] === 'true'
    return false
  }

  const acceptCookies = () => {
    document.cookie = 'accepted_cookie_agreement=' + 'true' + '; ' + 'Max-Age=31536000' + '; ' + document.cookie
    setCookiesAgreed(true)
  }

  React.useEffect(
    () => {
      history.listen((location) => {
        ym('hit', location.pathname)
      })
      setCookiesAgreed(hasCookiesAgreed())
      return () => {}
    },
    []
  )

  React.useEffect(() => {
    return () => {
      if (!location?.hash) window.scrollTo(0, 0)
    }
  }, [location])

  React.useEffect(() => {
    const href = parse(window.location.href, true)
    const utmCampaign = href.query.utm_campaign || ''
    const utmContent = href.query.utm_content || ''
    const utmMedium = href.query.utm_medium || ''
    const utmSource = href.query.utm_source || ''
    const utmTerm = href.query.utm_term || ''
    const rawPartnerId = href.query.partnerId || ''
    const hasPartnerId = !!rawPartnerId && !isNaN(+rawPartnerId.charAt(0))
    const partnerId = hasPartnerId ? Number.parseInt(rawPartnerId) + '' : ''
    const promo = href.query.promo || ''
    let preHostname = window.location.hostname.split('.').slice(1).join('.')
    if (preHostname) {
      preHostname = '.' + preHostname
    }
    const hostname = preHostname || window.location.hostname

    AmplitudeUserProp({ 'referral_value': partnerId ? partnerId : false })

    utmCampaign && !document.cookie.includes(`utm_campaign=${utmCampaign};`) &&
    (document.cookie = 'utm_campaign=' + utmCampaign + '; ' + 'Max-Age=100000' + '; ' +  'Domain=' + hostname + '; ' + document.cookie)
  utmContent && !document.cookie.includes(`utm_content=${utmContent};`) &&
    (document.cookie = 'utm_content=' + utmContent + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  utmMedium && !document.cookie.includes(`utm_medium=${utmMedium};`) &&
    (document.cookie = 'utm_medium=' + utmMedium + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  utmSource && !document.cookie.includes(`utm_source=${utmSource};`) &&
    (document.cookie = 'utm_source=' + utmSource + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  utmTerm && !document.cookie.includes(`utm_term=${utmTerm};`) &&
    (document.cookie = 'utm_term=' + utmTerm + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  partnerId && !document.cookie.includes(`partnerId=${partnerId};`) &&
    (document.cookie = 'partnerId=' + partnerId + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  promo && !document.cookie.includes(`promo=${promo};`) && (document.cookie = 'promo=' + promo + '; ' + 'Max-Age=100000' + '; ' + document.cookie) &&
    (document.cookie = 'promo=' + promo + '; ' + 'Max-Age=100000' + '; ' + 'Domain=' + hostname + '; ' + document.cookie)
  }, [])

  return (
    <DocumentTitle title={getTitle(location.pathname)}>
      <>
        <YMInitializer
          version='2'
          accounts={[631388]}
          options={{
            webvisor: true,
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            trackHash: true,
            // params: {
            //   'A/B_Test_site': process.env.TEST_SITE_VERSION === 'B' ? 'new_site' : 'old_site'
            // }
          }}
          // attrs={{
          //   'A/B_Test_site': process.env.TEST_SITE_VERSION === 'B' ? 'new_site' : 'old_site'
          // }}
        />
        {
          location.pathname !== '/new-year' && location.pathname !== '/new-year/' && <Menu />
        }
        <>
          {(location.pathname !== '/new-year' && location.pathname !== '/new-year/') && <div style={{height: '50px'}}/>}
          <Switch>
            {
              routes.map((route, index) =>
                  <Route key={index} exact path={route.path} component={route.component}/>
              )
            }
            <Redirect to='/404'/>
          </Switch>
        </>
        {
            (location.pathname !== '/new-year' && location.pathname !== '/new-year/') &&
            (country?.value === 'UZ' ? <FooterUZ /> : <Footer />)
        }
        {
          !cookiesAgreed && <CookieModal acceptCookies={acceptCookies} />
        }
        {(location.pathname !== '/new-year' && location.pathname !== '/new-year/') && <WhatsappWidget />}
      </>
    </DocumentTitle>
  )
}


export default MainRouter
